import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, Filter } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductImage from "@/components/ProductImage";
import { Input } from "@/components/ui/input";
import { fetchCategories, fetchProducts } from "@/lib/api";

export default function CatalogPage() {
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState("");

  const activeCat = searchParams.get("category") || "";

  useEffect(() => {
    fetchCategories().then(setCats).catch(() => {});
  }, []);

  useEffect(() => {
    const params = activeCat ? { category: activeCat } : {};
    fetchProducts(params).then(setProducts).catch(() => setProducts([]));
  }, [activeCat]);

  const filtered = useMemo(() => {
    if (!query.trim()) return products;
    const q = query.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        p.short_name.toLowerCase().includes(q) ||
        p.model_number.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q)
    );
  }, [products, query]);

  const setCategory = (slug) => {
    if (!slug) searchParams.delete("category");
    else searchParams.set("category", slug);
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div data-testid="catalog-page" className="min-h-screen bg-white text-[#0A1128]">
      <Header />
      <main className="pt-32 pb-24">
        {/* Page header */}
        <div className="border-b border-slate-200 bg-slate-50 tactical-grid">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
            <div className="overline mb-3 text-red-600">// Equipment Catalog</div>
            <h1 className="font-display font-black uppercase text-[#0A1128] text-5xl md:text-6xl tracking-tighter leading-[0.92]">
              The full <span className="text-red-600">arsenal.</span>
            </h1>
            <p className="text-slate-600 max-w-2xl mt-4">
              Browse our complete range of detection, EOD and search-surveillance equipment — manufactured by Master King Corps for the Indian armed forces, paramilitary, police and security agencies.
            </p>
          </div>
        </div>

        {/* Toolbar */}
        <div className="sticky top-20 z-30 border-b border-slate-200 bg-white/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div className="flex items-center gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
              <div className="flex items-center gap-2 mr-3 text-slate-500">
                <Filter className="w-4 h-4" />
                <span className="overline">Filter</span>
              </div>
              <button
                onClick={() => setCategory("")}
                data-testid="filter-all"
                className={`shrink-0 text-xs uppercase tracking-widest font-bold px-4 h-10 border ${
                  activeCat === "" ? "bg-red-600 border-red-600 text-white" : "border-slate-300 text-slate-700 hover:border-slate-500 bg-white"
                }`}
              >
                All
              </button>
              {cats.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => setCategory(c.slug)}
                  data-testid={`filter-${c.slug}`}
                  className={`shrink-0 text-xs uppercase tracking-widest font-bold px-4 h-10 border ${
                    activeCat === c.slug ? "bg-red-600 border-red-600 text-white" : "border-slate-300 text-slate-700 hover:border-slate-500 bg-white"
                  }`}
                >
                  {c.short_name || c.name}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="catalog-search"
                placeholder="Search by name or model…"
                className="pl-10 bg-white border-slate-300 rounded-none h-10 text-[#0A1128] placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-0 focus-visible:border-blue-600"
              />
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          {filtered.length === 0 ? (
            <div data-testid="empty-state" className="border border-slate-200 bg-slate-50 p-16 text-center">
              <div className="overline">// No results</div>
              <p className="text-slate-600 mt-3">No equipment matches your filters.</p>
            </div>
          ) : (
            <>
              <div className="mb-6 overline">{filtered.length} item{filtered.length !== 1 ? "s" : ""} · sorted by catalogue</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                {filtered.map((p, idx) => (
                  <Link
                    key={p.id}
                    to={`/products/${p.slug}`}
                    data-testid={`catalog-product-${p.slug}`}
                    className="group bg-white p-6 lift hover:bg-slate-50"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span className="font-mono-tactical text-[10px] tracking-[0.25em] text-slate-400 uppercase">
                        // {String(idx + 1).padStart(3, "0")}
                      </span>
                      <span className="overline">{p.category_name}</span>
                    </div>
                    <div className="relative aspect-square bg-slate-50 border border-slate-200 mb-5 flex items-center justify-center overflow-hidden">
                      <ProductImage product={p} />
                    </div>
                    <h3 className="font-display font-bold uppercase text-[#0A1128] text-base tracking-tight leading-tight mb-1">
                      {p.name}
                    </h3>
                    <div className="font-mono-tactical text-[10px] tracking-widest text-slate-500 mb-3">{p.model_number}</div>
                    <p className="text-slate-600 text-xs leading-relaxed mb-4 line-clamp-2">{p.tagline}</p>
                    <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-blue-600 group-hover:text-blue-700">
                      View details <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
